"use client";

import { useMemo } from "react";

import { PopoverClose } from "@/components/ui";

import { RESTYLANE_COLORS } from "@/lib/color";
import { cx } from "@/lib/tailwind-utils";

export type ColorPickerProps = {
  color: string | undefined;
  onChange: (hex: string) => void;
  className?: string;
  closeOnSelect?: boolean;
  palette?: readonly string[]; // Optional custom palette - if provided, uses this instead of default
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  className,
  closeOnSelect = false,
  palette,
}) => {
  const defaultPaletteRows = useMemo(
    () => [
      [
        "#EFF6FF",
        "#DBEAFE",
        "#BFDBFE",
        "#93C5FD",
        "#60A5FA",
        "#3B82F6",
        "#2563EB",
        "#1D4ED8",
        "#1E40AF",
        "#1E3A8A",
      ],
      [
        "#ECFDF5",
        "#D1FAE5",
        "#A7F3D0",
        "#6EE7B7",
        "#34D399",
        "#10B981",
        "#059669",
        "#047857",
        "#065F46",
        "#064E3B",
      ],
      [
        "#FFFBEB",
        "#FEF3C7",
        "#FDE68A",
        "#FCD34D",
        "#FBBF24",
        "#F59E0B",
        "#D97706",
        "#B45309",
        "#92400E",
        "#78350F",
      ],
      [
        "#FFFDF0",
        "#FEF9E7",
        "#FEF3C7",
        "#FDE68A",
        "#FACC15",
        "#EAB308",
        "#CA8A04",
        "#A16207",
        "#854D0E",
        "#713F12",
      ],
      [
        "#FEF2F2",
        "#FEE2E2",
        "#FECACA",
        "#FCA5A5",
        "#F87171",
        "#EF4444",
        "#DC2626",
        "#B91C1C",
        "#991B1B",
        "#7F1D1D",
      ],
      [
        "#FAF5FF",
        "#F3E8FF",
        "#E9D5FF",
        "#D8B4FE",
        "#C084FC",
        "#A855F7",
        "#9333EA",
        "#7E22CE",
        "#6B21A8",
        "#581C87",
      ],
      [
        "#ECFEFF",
        "#CFFAFE",
        "#A5F3FC",
        "#67E8F9",
        "#22D3EE",
        "#06B6D4",
        "#0891B2",
        "#0E7490",
        "#155E75",
        "#164E63",
      ],
      [
        "#F8FAFC",
        "#F1F5F9",
        "#E2E8F0",
        "#CBD5E1",
        "#94A3B8",
        "#64748B",
        "#475569",
        "#334155",
        "#1E293B",
        "#0F172A",
      ],
    ],
    [],
  );

  const current = color?.toLowerCase();

  const defaultColors = useMemo(
    () => defaultPaletteRows.flatMap((row) => row.slice(2, -1)),
    [defaultPaletteRows],
  );

  const renderColorButton = (c: string, index: number) => {
    const key = `${c}-${index}`;
    const btn = (
      <button
        key={key}
        aria-label={`Set color ${c}`}
        className={cx(
          "aspect-video h-4 rounded-full border border-border",
          current === c.toLowerCase() ? "ring-2 ring-ring ring-offset-1" : "",
        )}
        style={{ backgroundColor: c }}
        onClick={(e) => {
          e.preventDefault();
          if (current !== c.toLowerCase()) onChange(c);
        }}
      />
    );
    return closeOnSelect ? (
      <PopoverClose asChild key={key}>
        {btn}
      </PopoverClose>
    ) : (
      btn
    );
  };

  if (palette) {
    return (
      <div className={cx("flex flex-col gap-2", className)}>
        <div className="grid grid-cols-7 gap-x-3 gap-y-2">
          {palette.map(renderColorButton)}
        </div>
      </div>
    );
  }

  return (
    <div className={cx("flex flex-col gap-3", className)}>
      <div className="grid grid-cols-7 gap-x-3 gap-y-2">
        {defaultColors.map(renderColorButton)}
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-tiny text-foreground-secondary">Restylane</span>
        <div className="grid grid-cols-7 gap-x-3 gap-y-2">
          {RESTYLANE_COLORS.map(renderColorButton)}
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
